import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  PlayCircle,
  Sparkles,
  Brain,
  ShieldAlert,
  Workflow,
  TrendingUp,
  RotateCw,
  Database,
  Network,
  Cpu,
  Target,
  Zap,
  Activity,
  CheckCircle2,
} from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { SEO } from "@/components/SEO";

/* ───────────────────────────── Data ───────────────────────────── */
const lifecycle = [
  { stage: "Underwriting", aiLabel: "Risk Scoring" },
  { stage: "Policy", aiLabel: "Pricing Optimization" },
  { stage: "Claims", aiLabel: "Claims Automation" },
  { stage: "Servicing", aiLabel: "Customer Insights" },
  { stage: "Renewals", aiLabel: "Renewal Propensity" },
];

const useCases = [
  { Icon: Sparkles, title: "AI-Driven Claims Automation", body: "Automate FNOL to settlement with intelligent triaging and decision support." },
  { Icon: Brain, title: "Underwriting Intelligence", body: "Enhance underwriting accuracy with dynamic risk scoring models." },
  { Icon: ShieldAlert, title: "Fraud Detection & Prevention", body: "Identify anomalies and reduce claims leakage in real time." },
  { Icon: Workflow, title: "Operational Automation", body: "Drive efficiency through AI-led workflow orchestration." },
  { Icon: TrendingUp, title: "Customer & Cross-Sell Insights", body: "Identify opportunities for upsell and cross-sell across portfolios." },
  { Icon: RotateCw, title: "Renewal & Persistency Models", body: "Predict renewals and improve customer retention strategies." },
];

const howItWorks = [
  { Icon: Database, title: "Data Layer", body: "Policy, claims, customer and partner data." },
  { Icon: Cpu, title: "AI Models", body: "Risk scoring, fraud detection, predictions." },
  { Icon: Target, title: "Decision Engine", body: "Rule + AI-based decisioning." },
  { Icon: Workflow, title: "Workflow Integration", body: "Embedded into underwriting and claims processes." },
  { Icon: Zap, title: "Business Outcomes", body: "Faster processing, better decisions." },
];

const impact = [
  { Icon: Zap, title: "Faster Claims Processing", body: "Reduce turnaround time with automation." },
  { Icon: Target, title: "Improved Underwriting Accuracy", body: "Better risk selection and pricing." },
  { Icon: ShieldAlert, title: "Reduced Fraud Leakage", body: "Detect anomalies early." },
  { Icon: Activity, title: "Operational Efficiency", body: "Lower cost through automation." },
];

const proof = [
  { value: "40M+", label: "Data points powering AI models", sub: "Across policies, claims and partner streams" },
  { value: "End-to-End", label: "Underwriting + Claims workflows", sub: "Deployed natively in the core platform" },
  { value: "Continuous", label: "Learning and model improvement", sub: "Feedback loops and tenant-specific tuning" },
];

/* ───────────────────────────── Page ───────────────────────────── */
export default function IntelligencePage() {
  return (
    <main className="bg-white text-ink" data-testid="intelligence-page">
      <SEO
        title="Gradatim Intelligence® — AI for Insurance Operations"
        description="Gradatim Intelligence®: embedded operational AI for claims, fraud, persistency, underwriting and decision support across the insurance lifecycle."
        path="/products/intelligence"
      />
      <Nav />

      {/* HERO */}
      <section data-nav-dark className="relative isolate bg-brand-950 text-white overflow-hidden grain-overlay min-h-[80svh] flex items-center">
        <div aria-hidden className="absolute inset-0 bg-brand-950" />
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(167,139,250,0.18),transparent_55%)]" />
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(200,75,49,0.22),transparent_60%)]" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <div className="mb-6">
              <span className="overline text-copper">Gradatim Intelligence®</span>
            </div>
            <h1
              data-testid="intel-hero-title"
              className="font-display font-medium text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight"
            >
              AI, Embedded Across the
              <br />
              <span className="text-copper">Insurance Lifecycle</span>
              <span className="text-copper">.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl text-slate-300/90 leading-relaxed font-light">
              An AI-powered intelligence layer integrated into the core platform —
              enabling smarter decisions, automated operations and continuous
              optimization across underwriting, claims and servicing.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href="#use-cases"
                data-testid="intel-hero-primary-cta"
                className="group inline-flex items-center justify-center gap-3 bg-copper hover:bg-copper-600 text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              >
                Explore AI capabilities
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <Link
                to="/#contact"
                data-testid="intel-hero-secondary-cta"
                className="group inline-flex items-center justify-center gap-3 border-2 border-copper text-copper hover:bg-copper hover:text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              >
                <PlayCircle className="h-4 w-4" />
                Request a demo
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 hidden lg:block"
          >
            <NeuralVisual />
          </motion.div>
        </div>
      </section>

      {/* AI ACROSS LIFECYCLE */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="overline text-copper">AI Across Lifecycle</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5 mb-5">
              AI Across the <span className="text-copper">Insurance Value Chain</span>
            </h2>
            <p className="text-base md:text-[17px] text-slate-600 leading-relaxed">
              AI augments every stage of the insurance lifecycle — driving
              efficiency, accuracy and speed.
            </p>
          </div>

          <div className="flex flex-col md:flex-row md:items-stretch md:justify-between gap-4 md:gap-2 relative">
            {lifecycle.map((stage, i) => (
              <div key={stage.stage} className="flex flex-col md:flex-row items-center md:flex-1">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.08, duration: 0.55 }}
                  className="group flex-1 w-full md:w-auto bg-cream border border-hairline hover:border-copper/40 transition-colors rounded-xl p-5 text-center"
                  data-testid={`lifecycle-${i}`}
                >
                  <div className="font-display text-[19px] md:text-[20px] tracking-tight text-ink mb-2">
                    {stage.stage}
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-[12px] text-copper">
                    <Sparkles className="h-3 w-3" strokeWidth={2} />
                    {stage.aiLabel}
                  </div>
                </motion.div>
                {i < lifecycle.length - 1 && (
                  <>
                    <ArrowRight className="h-5 w-5 text-slate-300 mx-2 hidden md:block shrink-0" />
                    <ArrowDown className="h-4 w-4 text-slate-300 my-2 md:hidden" />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE AI USE CASES */}
      <section id="use-cases" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F5F7FA 100%)" }} className="border-y border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="overline text-copper">Core AI Use Cases</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
              High-Impact <span className="text-copper">AI Use Cases</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {useCases.map((u, i) => (
              <motion.div
                key={u.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.06, duration: 0.55 }}
                className="group bg-cream border border-hairline rounded-[14px] p-5 md:p-6 hover:border-copper/40 hover:bg-white transition-colors"
                data-testid={`intel-usecase-${i}`}
              >
                <div className="h-11 w-11 bg-white border border-hairline group-hover:border-copper flex items-center justify-center mb-5 transition-colors">
                  <u.Icon className="h-5 w-5 text-copper" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-[18px] md:text-[20px] tracking-tight text-ink mb-2">{u.title}</h3>
                <p className="text-[14px] text-slate-600 leading-relaxed">{u.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="overline text-copper">How It Works</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
              Built Into the <span className="text-copper">Core Platform</span>
            </h2>
          </div>

          {/* Flow */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-2">
            {howItWorks.map((s, i) => (
              <div key={s.title} className="relative">
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className={`group h-full rounded-xl p-5 md:p-6 border transition-colors ${
                    i === howItWorks.length - 1
                      ? "bg-ink text-white border-ink"
                      : "bg-cream border-hairline hover:border-copper/40"
                  }`}
                  data-testid={`how-step-${i}`}
                >
                  <div className={`h-11 w-11 flex items-center justify-center border mb-4 ${
                    i === howItWorks.length - 1
                      ? "bg-white/10 border-white/20"
                      : "bg-white border-hairline group-hover:border-copper"
                  }`}>
                    <s.Icon className="h-5 w-5 text-copper" strokeWidth={1.6} />
                  </div>
                  <div className={`overline mb-2 ${i === howItWorks.length - 1 ? "text-copper" : "text-slate-400"}`}>
                    Step 0{i + 1}
                  </div>
                  <h3 className={`font-display text-[17px] tracking-tight leading-tight mb-2 ${
                    i === howItWorks.length - 1 ? "text-white" : "text-ink"
                  }`}>
                    {s.title}
                  </h3>
                  <p className={`text-[13px] leading-relaxed ${
                    i === howItWorks.length - 1 ? "text-slate-300" : "text-slate-600"
                  }`}>
                    {s.body}
                  </p>
                </motion.div>
                {i < howItWorks.length - 1 && (
                  <div className="hidden md:flex absolute top-1/2 -right-2 -translate-y-1/2 z-10">
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS IMPACT */}
      <section style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F5F7FA 100%)" }} className="border-y border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="overline text-copper">Business Impact</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
              Driving Measurable <span className="text-copper">Business Outcomes</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {impact.map((m, i) => (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.07, duration: 0.55 }}
                className="bg-white border border-hairline rounded-[14px] p-6 hover:border-copper/40 transition-colors"
                data-testid={`impact-${i}`}
              >
                <div className="h-11 w-11 bg-cream border border-hairline flex items-center justify-center mb-5">
                  <m.Icon className="h-5 w-5 text-copper" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-[18px] tracking-tight text-ink mb-2 leading-tight">{m.title}</h3>
                <p className="text-[14px] text-slate-600 leading-relaxed">{m.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROOF / SCALE */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="overline text-copper">Proof &amp; Scale</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
              AI at <span className="text-copper">Enterprise Scale</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {proof.map((p, i) => (
              <motion.div
                key={p.value}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.08, duration: 0.55 }}
                className="bg-cream border border-hairline rounded-2xl p-7 text-center hover:border-copper/40 transition-colors"
                data-testid={`intel-proof-${i}`}
              >
                <div className="font-display text-4xl md:text-[44px] tracking-tight text-ink leading-tight mb-4">
                  {p.value}
                </div>
                <div className="font-display text-[17px] tracking-tight text-copper mb-2 font-medium">
                  {p.label}
                </div>
                <p className="text-[13px] md:text-[14px] text-slate-600 leading-relaxed">{p.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-nav-dark className="relative bg-ink text-white overflow-hidden grain-overlay">
        <div aria-hidden className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-copper/15 blur-3xl" />
        <div aria-hidden className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-violet-500/10 blur-3xl" />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <h2 className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.05]">
            Ready to <span className="text-copper">Unlock the Power of AI?</span>
          </h2>
          <p className="text-base md:text-[18px] text-slate-300 leading-relaxed mt-6 max-w-2xl mx-auto">
            Engage with our team to explore how Gradatim Intelligence® can transform
            your insurance operations with AI-driven automation and decision-making.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/#contact"
              className="group inline-flex items-center justify-center gap-3 bg-copper hover:bg-copper-600 text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              data-testid="intel-final-cta-primary"
            >
              Request a demo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#contact"
              className="group inline-flex items-center justify-center gap-3 border-2 border-white/30 hover:border-copper hover:text-copper text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              data-testid="intel-final-cta-secondary"
            >
              See AI in action
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

/* ───────────────────────────── Visual ───────────────────────────── */
/** Hero neural-network visual: glowing node clusters with flowing lines */
const NeuralVisual = () => {
  const layers = [
    [80, 140, 200, 260, 320],
    [110, 180, 250, 320],
    [140, 220, 300],
    [200],
  ];
  const xs = [60, 150, 240, 340];

  return (
    <div className="relative aspect-square w-full max-w-[480px] mx-auto">
      <div aria-hidden className="absolute -inset-10 bg-[radial-gradient(circle_at_center,rgba(167,139,250,0.22),transparent_60%)]" />
      <div className="absolute inset-0 border border-white/10" />
      <div className="absolute inset-3 border border-white/5" />

      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" aria-hidden>
        {/* Connection lines */}
        {layers.slice(0, -1).map((layer, li) =>
          layer.map((y1, ai) =>
            layers[li + 1].map((y2, bi) => (
              <line
                key={`l-${li}-${ai}-${bi}`}
                x1={xs[li]}
                y1={y1}
                x2={xs[li + 1]}
                y2={y2}
                stroke="rgba(200,75,49,0.25)"
                strokeWidth="0.6"
              />
            ))
          )
        )}
        {/* Nodes */}
        {layers.map((layer, li) =>
          layer.map((y, ni) => (
            <g key={`n-${li}-${ni}`}>
              <circle cx={xs[li]} cy={y} r="8" fill="rgba(200,75,49,0.15)" />
              <circle cx={xs[li]} cy={y} r="4" fill="#C84B31" opacity={0.85} />
            </g>
          ))
        )}
        {/* Output halo */}
        <circle cx={xs[3]} cy={layers[3][0]} r="24" fill="#C84B31" opacity="0.18" />
      </svg>

      {/* Output label */}
      <div className="absolute right-6 top-1/2 -translate-y-1/2">
        <div className="bg-ink/80 backdrop-blur-md border border-white/15 px-3 py-2 text-right">
          <div className="overline text-copper">Output</div>
          <div className="font-display text-base text-white">Decision</div>
        </div>
      </div>

      {/* Input label */}
      <div className="absolute left-6 top-6">
        <div className="bg-ink/80 backdrop-blur-md border border-white/15 px-3 py-2">
          <div className="overline text-copper">Input</div>
          <div className="font-display text-base text-white">Data Streams</div>
        </div>
      </div>
    </div>
  );
};
